import { getCurrentWorkspace } from "@/lib/workspaceBranding";
/**
 * Compact LBS ticket reply signature (email-safe table layout).
 * Sized to render cleanly in Gmail, Outlook, Apple Mail (~480px wide).
 * Photo/logo/social URLs optional — edit in Settings → Tickets → Reply signature.
 *
 * Gmail blocks SVG data URIs — logo and social icons use PNG/JPEG data URIs.
 */

import {
  LBS_EMAIL_SOCIAL_FACEBOOK_PNG,
  LBS_EMAIL_SOCIAL_LINKEDIN_PNG,
  LBS_EMAIL_SOCIAL_TIKTOK_PNG,
  LBS_EMAIL_SOCIAL_X_PNG,
} from "@/modules/tickets/lbsSignatureEmailAssets";

export const LBS_SIGNATURE_ACCENT = "#222222";
export const LBS_SIGNATURE_NAVY = "#1e3a6e";
/** Presets shown in Settings (business-neutral defaults). */
export const LBS_SIGNATURE_ACCENT_PRESETS = [
  { id: "black", label: "Black", color: "#222222" },
  { id: "gray", label: "Gray", color: "#4b5563" },
  { id: "navy", label: "Navy", color: "#1e3a6e" },
] as const;

export const LBS_DEFAULT_SIGNATURE_NAME = "Latinos Business Support";
export const LBS_DEFAULT_SIGNATURE_TITLE =
  "Marketing Digital · Web · Xactimate";
export const LBS_DEFAULT_SIGNATURE_PHONE = "(203) 303-9148";
export const LBS_DEFAULT_SIGNATURE_EMAIL = "info@lbs.bz";
export const LBS_DEFAULT_SIGNATURE_WEBSITE = "www.lbs.bz";
export const LBS_DEFAULT_SIGNATURE_WEBSITE_HREF = "https://www.lbs.bz";
export const LBS_DEFAULT_SIGNATURE_ADDRESS =
  "1200 Summer St, Stamford, 06902 CT";

/** LBS blue wordmark (uploaded 2026-10-09), served by the CRM host. */
export const LBS_DEFAULT_SIGNATURE_LOGO_URL =
  "https://www.nomicrm.com/logos/lbs-logo-blue.png";

/**
 * Retired LBS logos (removed or 404). Only these exact images are replaced;
 * a logo uploaded in Settings (also a data URI) is never touched.
 */
const RETIRED_LBS_LOGO_PATTERN =
  /(nomicrm\.com\/logos\/latino-business-support\.jpg|lbs\.bz\/lbs-logo-black\.png|3dgWje4Ve8Vbj3jJ4\+FS6iJHbZLKGGktHkoCAEn5V9PMNPp2OtocTkHCkgjP)/i;
export const LBS_DEFAULT_SIGNATURE_FACEBOOK_URL =
  "https://www.facebook.com/lbsupp";
export const LBS_DEFAULT_SIGNATURE_LINKEDIN_URL =
  "https://www.linkedin.com/in/latino-business-support-8a349b37b";
export const LBS_DEFAULT_SIGNATURE_TIKTOK_URL =
  "https://www.tiktok.com/@latinos_business_support";
export const LBS_DEFAULT_SIGNATURE_X_URL = "https://x.com/LatinoBusinessS";

export const LBS_SIGNATURE_DISCLAIMER =
  "The content of this email is confidential and intended for the recipient specified in message only. It is strictly forbidden to share any part of this message with any third party, without a written consent of the sender. If you received this message by mistake, please reply to this message and follow with its deletion, so that we can ensure such a mistake does not occur in the future.";

/** Fixed email-safe sizes (px). Keep the band ~70px tall like the reference. */
const PHOTO_SIZE = 68;
/** Match export canvas aspect: circle 136 + overhang 52 → ~94px tall at 68px wide. */
const PHOTO_DISPLAY_HEIGHT = 94;
const LOGO_WIDTH = 96;
const SIGNATURE_WIDTH = 500;
const SOCIAL_ICON_SIZE = 20;

/** Email-safe accent normalizer (hex). */
const normalizeAccentColor = (value?: string | null) => {
  const trimmed = value?.trim() ?? "";
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [, a, b, c] = trimmed;
    return `#${a}${a}${b}${b}${c}${c}`.toLowerCase();
  }
  return LBS_SIGNATURE_ACCENT;
};

/** Prefer email-safe PNG data URIs (Gmail strips SVG data URIs). */
export const buildLbsSocialIcon = (
  network: "facebook" | "linkedin" | "tiktok" | "x",
  _accent = LBS_SIGNATURE_ACCENT,
) => {
  switch (network) {
    case "facebook":
      return LBS_EMAIL_SOCIAL_FACEBOOK_PNG;
    case "linkedin":
      return LBS_EMAIL_SOCIAL_LINKEDIN_PNG;
    case "tiktok":
      return LBS_EMAIL_SOCIAL_TIKTOK_PNG;
    case "x":
      return LBS_EMAIL_SOCIAL_X_PNG;
  }
};

export type LbsReplySignatureConfig = {
  name?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  websiteHref?: string | null;
  address?: string | null;
  /** Hex accent for photo ring, title, and social icon circles. */
  accentColor?: string | null;
  /** Absolute https URL — leave empty until you upload a headshot. */
  photoUrl?: string | null;
  /** Absolute https URL for company logo. */
  logoUrl?: string | null;
  facebookUrl?: string | null;
  linkedinUrl?: string | null;
  tiktokUrl?: string | null;
  xUrl?: string | null;
};

/** Config after defaults: every field is a string ("" hides it). */
export type NormalizedLbsReplySignatureConfig = {
  [K in keyof LbsReplySignatureConfig]-?: string;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const attr = (value: string) => escapeHtml(value);

/** Keep http(s) links only; blank clears the icon. */
const normalizeHttpUrl = (value?: string | null, fallback = "") => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return fallback;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^(facebook|linkedin|tiktok|x|twitter|www)\./i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;
};

/** Keep http(s) links only. undefined → fallback; "" → hide icon. */
const normalizeSocialUrl = (
  value: string | null | undefined,
  fallback: string,
) => {
  if (value === undefined || value === null) return fallback;
  const trimmed = value.trim();
  if (!trimmed) return "";
  return normalizeHttpUrl(trimmed, fallback);
};

/**
 * Whose signature defaults apply (Phase 4, docs/plans/MULTI-TENANT-MODULES.md).
 * The home org (LBS) keeps the LBS details below as fallbacks; any other
 * company only ever gets its own name — never LBS's phone, logo or links.
 * Workspace comes from setCurrentWorkspace (authProvider).
 */
export const isHomeReplySignatureWorkspace = () => getCurrentWorkspace().isHome;

export const replySignatureWorkspaceName = () =>
  getCurrentWorkspace().name ||
  (getCurrentWorkspace().isHome ? LBS_DEFAULT_SIGNATURE_NAME : "");

/** Footer under the default signature of companies without their own. */
export const SENT_WITH_PRODUCT_LABEL = "Sent with Sigma CRM";
const SENT_WITH_PRODUCT_HREF = "https://www.nomicrm.com";

/**
 * A company that has not set up a reply signature (Settings → Tickets):
 * its name and a quiet "Sent with Sigma CRM" line. Never LBS's details.
 */
export const buildWorkspaceFallbackSignatureHtml = () => {
  const name = replySignatureWorkspaceName();
  const nameHtml = name
    ? `<div style="font-weight:600;color:#111827;">${escapeHtml(name)}</div>`
    : "";
  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:13px;line-height:1.5;color:#374151;">${nameHtml}<div style="margin-top:6px;font-size:11px;color:#9ca3af;">Sent with <a href="${SENT_WITH_PRODUCT_HREF}" style="color:#9ca3af;text-decoration:underline;">Sigma CRM</a></div></div>`;
};

export const buildWorkspaceFallbackSignaturePlain = () =>
  [replySignatureWorkspaceName(), SENT_WITH_PRODUCT_LABEL]
    .filter(Boolean)
    .join("\n\n");

const signatureDefaults = () =>
  getCurrentWorkspace().isHome
    ? {
        name: LBS_DEFAULT_SIGNATURE_NAME,
        title: LBS_DEFAULT_SIGNATURE_TITLE,
        phone: LBS_DEFAULT_SIGNATURE_PHONE,
        email: LBS_DEFAULT_SIGNATURE_EMAIL,
        website: LBS_DEFAULT_SIGNATURE_WEBSITE,
        websiteHref: LBS_DEFAULT_SIGNATURE_WEBSITE_HREF,
        address: LBS_DEFAULT_SIGNATURE_ADDRESS,
        logoUrl: LBS_DEFAULT_SIGNATURE_LOGO_URL,
        facebookUrl: LBS_DEFAULT_SIGNATURE_FACEBOOK_URL,
        linkedinUrl: LBS_DEFAULT_SIGNATURE_LINKEDIN_URL,
        tiktokUrl: LBS_DEFAULT_SIGNATURE_TIKTOK_URL,
        xUrl: LBS_DEFAULT_SIGNATURE_X_URL,
      }
    : {
        name: getCurrentWorkspace().name,
        title: "",
        phone: "",
        email: "",
        website: "",
        websiteHref: "",
        address: "",
        logoUrl: "",
        facebookUrl: "",
        linkedinUrl: "",
        tiktokUrl: "",
        xUrl: "",
      };

export const normalizeLbsReplySignatureConfig = (
  config?: LbsReplySignatureConfig | null,
): NormalizedLbsReplySignatureConfig => {
  const d = signatureDefaults();
  return {
    name: config?.name?.trim() || d.name,
    title: config?.title?.trim() || d.title,
    phone: config?.phone?.trim() || d.phone,
    email: config?.email?.trim() || d.email,
    website: config?.website?.trim() || d.website,
    websiteHref:
      config?.websiteHref?.trim() ||
      (config?.website?.trim()
        ? config.website.trim().startsWith("http")
          ? config.website.trim()
          : `https://${config.website.trim()}`
        : d.websiteHref),
    address: config?.address?.trim() || d.address,
    accentColor: normalizeAccentColor(config?.accentColor),
    photoUrl: config?.photoUrl?.trim() || "",
    logoUrl: (() => {
      const trimmed = config?.logoUrl?.trim() || "";
      if (!trimmed || RETIRED_LBS_LOGO_PATTERN.test(trimmed)) {
        return d.logoUrl;
      }
      return trimmed;
    })(),
    facebookUrl: normalizeSocialUrl(config?.facebookUrl, d.facebookUrl),
    linkedinUrl: normalizeSocialUrl(config?.linkedinUrl, d.linkedinUrl),
    tiktokUrl: normalizeSocialUrl(config?.tiktokUrl, d.tiktokUrl),
    xUrl: normalizeSocialUrl(config?.xUrl, d.xUrl),
  };
};

export const lbsReplySignaturePlainText = (
  config?: LbsReplySignatureConfig | null,
) => {
  const c = normalizeLbsReplySignatureConfig(config);
  return [
    [c.name, c.title].filter(Boolean).join(" | "),
    [c.phone, c.email, c.website].filter(Boolean).join(" | "),
    c.address,
  ]
    .filter(Boolean)
    .join("\n");
};

const photoCell = (photoUrl: string, name: string, accent: string) => {
  const cellPad = `padding:0 12px 0 0;width:${PHOTO_SIZE + 12}px;`;

  if (photoUrl) {
    // Pop-out PNGs are taller than wide (transparent top overhang + circle).
    return `<td width="${PHOTO_SIZE + 12}" valign="top" style="${cellPad}">
  <img data-lbs-signature-photo="true" src="${attr(photoUrl)}" width="${PHOTO_SIZE}" height="${PHOTO_DISPLAY_HEIGHT}" alt="${attr(name)}" style="display:block;width:${PHOTO_SIZE}px !important;height:auto !important;max-width:${PHOTO_SIZE}px !important;border:0;" />
</td>`;
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return `<td width="${PHOTO_SIZE + 12}" valign="middle" style="${cellPad}">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${PHOTO_SIZE}" height="${PHOTO_SIZE}" style="width:${PHOTO_SIZE}px;height:${PHOTO_SIZE}px;border-collapse:separate;border:2px solid ${accent};border-radius:50%;background:#f3f4f6;">
    <tr>
      <td align="center" valign="middle" height="${PHOTO_SIZE}" style="height:${PHOTO_SIZE}px;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:700;color:${accent};line-height:${PHOTO_SIZE}px;">${attr(initials || "LBS")}</td>
    </tr>
  </table>
</td>`;
};

const contactLineHtml = (c: NormalizedLbsReplySignatureConfig) => {
  const phoneHref = `tel:${c.phone.replace(/[^\d+]/g, "")}`;
  const parts = [
    c.phone
      ? `<a href="${attr(phoneHref)}" style="color:#222222;text-decoration:none;">${attr(c.phone)}</a>`
      : "",
    c.email
      ? `<a href="${attr(`mailto:${c.email}`)}" style="color:#222222;text-decoration:none;">${attr(c.email)}</a>`
      : "",
    c.website
      ? `<a href="${attr(c.websiteHref)}" style="color:#222222;text-decoration:none;">${attr(c.website)}</a>`
      : "",
  ].filter(Boolean);
  if (!parts.length) return "";
  return `<p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:11px;line-height:1.4;color:#222222;">${parts.join(" | ")}</p>`;
};

const socialIcon = (href: string, iconUrl: string, title: string) =>
  `<a href="${attr(href)}" title="${attr(title)}" target="_blank" style="display:inline-block;margin:0 3px;text-decoration:none;border:0;">
  <img data-lbs-signature-photo="true" src="${attr(iconUrl)}" width="${SOCIAL_ICON_SIZE}" height="${SOCIAL_ICON_SIZE}" alt="${attr(title)}" style="display:block;width:${SOCIAL_ICON_SIZE}px;height:${SOCIAL_ICON_SIZE}px;border:0;" />
</a>`;

const buildSocialIconsHtml = (c: NormalizedLbsReplySignatureConfig) => {
  const accent = c.accentColor;
  const parts: string[] = [];
  if (c.facebookUrl) {
    parts.push(
      socialIcon(
        c.facebookUrl,
        buildLbsSocialIcon("facebook", accent),
        "Facebook",
      ),
    );
  }
  if (c.linkedinUrl) {
    parts.push(
      socialIcon(
        c.linkedinUrl,
        buildLbsSocialIcon("linkedin", accent),
        "LinkedIn",
      ),
    );
  }
  if (c.tiktokUrl) {
    parts.push(
      socialIcon(c.tiktokUrl, buildLbsSocialIcon("tiktok", accent), "TikTok"),
    );
  }
  if (c.xUrl) {
    parts.push(socialIcon(c.xUrl, buildLbsSocialIcon("x", accent), "X"));
  }
  if (!parts.length) return "";
  return `<p style="margin:8px 0 0;line-height:${SOCIAL_ICON_SIZE}px;font-size:0;white-space:nowrap;">${parts.join("")}</p>`;
};

/**
 * Compact 3-column signature (~500px band + disclaimer).
 * Marked with data-lbs-signature for detection / settings round-trip.
 */
export const buildDefaultLbsReplySignatureHtml = (
  config?: LbsReplySignatureConfig | null,
) => {
  const c = normalizeLbsReplySignatureConfig(config);
  const accent = c.accentColor;

  return `<table data-lbs-signature="true" data-lbs-name="${attr(c.name)}" data-lbs-title="${attr(c.title)}" data-lbs-phone="${attr(c.phone)}" data-lbs-email="${attr(c.email)}" data-lbs-website="${attr(c.website)}" data-lbs-website-href="${attr(c.websiteHref)}" data-lbs-address="${attr(c.address)}" data-lbs-accent-color="${attr(accent)}" data-lbs-photo-url="${attr(c.photoUrl)}" data-lbs-logo-url="${attr(c.logoUrl)}" data-lbs-facebook-url="${attr(c.facebookUrl)}" data-lbs-linkedin-url="${attr(c.linkedinUrl)}" data-lbs-tiktok-url="${attr(c.tiktokUrl)}" data-lbs-x-url="${attr(c.xUrl)}" role="presentation" cellpadding="0" cellspacing="0" border="0" width="${SIGNATURE_WIDTH}" style="border-collapse:collapse;width:${SIGNATURE_WIDTH}px;max-width:100%;font-family:Arial,Helvetica,sans-serif;">
  <tr>
    <td style="padding:0 0 10px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;width:100%;">
        <tr>
          ${photoCell(c.photoUrl, c.name, accent)}
          <td width="1" bgcolor="#c4c4c4" style="width:1px;background:#c4c4c4;font-size:0;line-height:0;">&nbsp;</td>
          <td valign="middle" style="padding:0 14px;">
            <p style="margin:0 0 2px;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:700;line-height:1.25;color:#111111;">${attr(c.name)}</p>
            <p style="margin:0 0 4px;font-family:Arial,Helvetica,sans-serif;font-size:12px;font-weight:600;line-height:1.3;color:${accent};">${attr(c.title)}</p>
            ${contactLineHtml(c)}
          </td>
          <td width="132" valign="middle" align="center" style="padding:0;width:132px;text-align:center;">
            ${
              c.logoUrl
                ? `<img data-lbs-signature-photo="true" src="${attr(c.logoUrl)}" width="${LOGO_WIDTH}" alt="${attr(c.name)}" style="display:block;margin:0 auto 6px;width:${LOGO_WIDTH}px !important;max-width:${LOGO_WIDTH}px !important;height:auto !important;border:0;" />`
                : ""
            }
            ${buildSocialIconsHtml(c)}
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:8px 0 0;border-top:1px solid #e5e7eb;font-family:Arial,Helvetica,sans-serif;font-size:9px;line-height:1.4;color:#9ca3af;">
      ${attr(LBS_SIGNATURE_DISCLAIMER)}
      ${c.address ? ` · ${attr(c.address)}` : ""}
    </td>
  </tr>
</table>`;
};

export const parseLbsReplySignatureConfigFromHtml = (
  html?: string | null,
): LbsReplySignatureConfig | null => {
  const source = html?.trim();
  if (!source || !source.includes("data-lbs-signature")) return null;

  const read = (name: string) => {
    const match = source.match(new RegExp(`data-lbs-${name}="([^"]*)"`, "i"));
    if (!match) return undefined;
    return (match[1] ?? "")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&");
  };

  return {
    name: read("name") ?? "",
    title: read("title") ?? "",
    phone: read("phone") ?? "",
    email: read("email") ?? "",
    website: read("website") ?? "",
    websiteHref: read("website-href") ?? "",
    address: read("address") ?? "",
    photoUrl: read("photo-url") ?? "",
    logoUrl: read("logo-url") ?? "",
    accentColor: read("accent-color"),
    facebookUrl: read("facebook-url"),
    linkedinUrl: read("linkedin-url"),
    tiktokUrl: read("tiktok-url"),
    xUrl: read("x-url"),
  };
};

export const isLbsDefaultSignatureHtml = (html?: string | null) =>
  Boolean(html?.includes('data-lbs-signature="true"'));
